import * as React from "react";
import { cn } from "../lib/utils";
import { Card, CardContent } from "./card";
import { cva } from "class-variance-authority";
import { ArrowUpRight } from "lucide-react";
import { Sparkline } from "./sparkline";

const statCardIconVariants = cva(
  "rounded-xl p-3 transition-colors duration-200",
  {
    variants: {
      variant: {
        default: "bg-primary/10 text-primary",
        success: "bg-success/10 text-success",
        warning: "bg-warning/10 text-warning",
        error: "bg-destructive/10 text-destructive",
        info: "bg-info/10 text-info",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const StatCard = React.forwardRef(
  (
    {
      className,
      title,
      value,
      icon: Icon,
      iconVariant = "default",
      trend,
      sparklineData,
      sparkline,
      series,
      sparklineColor,
      onClick,
      ...props
    },
    ref,
  ) => {
    const rawSparkline = sparklineData || sparkline || series;
    const hasSparkline = Array.isArray(rawSparkline) && rawSparkline.length > 0;

    const defaultSparklineColor =
      sparklineColor ||
      (trend?.value !== undefined
        ? trend.value >= 0
          ? "var(--color-success, #10b981)"
          : "var(--color-destructive, #be3a2c)"
        : iconVariant === "success"
          ? "var(--color-success, #10b981)"
          : iconVariant === "warning"
            ? "var(--color-warning, #b86b19)"
            : iconVariant === "error"
              ? "var(--color-destructive, #be3a2c)"
              : iconVariant === "info"
                ? "var(--color-info, #537185)"
                : "var(--color-primary, #c25237)");

    const handleKeyDown = React.useCallback(
      (e) => {
        if (e.target !== e.currentTarget) return;
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(e);
        }
      },
      [onClick],
    );

    return (
      <Card
        ref={ref}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? handleKeyDown : undefined}
        className={cn(
          "group relative overflow-hidden transition-all duration-200",
          "border-border hover:border-border hover:bg-muted/20",
          onClick &&
            "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          className,
        )}
        onClick={onClick}
        {...props}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-muted-foreground leading-tight">
                {title}
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold tracking-tight text-foreground tabular-nums">
                  {value}
                </p>
                {onClick && (
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                )}
              </div>
              {trend && (
                <div
                  className={cn(
                    "flex items-center gap-1 text-xs font-medium tabular-nums",
                    trend.value > 0 ? "text-success" : "text-destructive",
                  )}
                >
                  <span>{trend.value > 0 ? "↑" : "↓"}</span>
                  <span>{Math.abs(trend.value)}%</span>
                  {trend.label && (
                    <span className="text-muted-foreground">{trend.label}</span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {Icon && (
                <div
                  className={cn(statCardIconVariants({ variant: iconVariant }))}
                >
                  <Icon className="h-5 w-5" />
                </div>
              )}
              {hasSparkline && (
                <div className="w-20 sm:w-24 mt-1">
                  <Sparkline
                    data={rawSparkline}
                    color={defaultSparklineColor}
                    height={26}
                    width="100%"
                    showDot={true}
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  },
);
StatCard.displayName = "StatCard";

export { StatCard };

